/**
 * FilterPopover — change-type, semantic-tag and review-status filter control
 * for the comparison workspace top bar.
 *
 * Renders a trigger button (matching TopBar peer controls) that opens an
 * absolute-positioned panel. No external popover library — uses a plain
 * boolean open/close state and a document-click handler to dismiss.
 */
import { useEffect, useRef, useState } from 'react';

import type { ChangeType, DecisionStatus, SemanticTag } from '../../lib/veridoc-api';
import type { ChangeStats } from './alignment';

interface FilterPopoverProps {
  typeFilter: Set<ChangeType>;
  statusFilter: Set<DecisionStatus>;
  semanticTagFilter: Set<SemanticTag>;
  onToggleType: (type: ChangeType) => void;
  onToggleStatus: (status: DecisionStatus) => void;
  onToggleSemanticTag: (tag: SemanticTag) => void;
  onReset: () => void;
  isFiltering: boolean;
  activeFilterCount: number;
  stats: ChangeStats;
  /**
   * 弹出对话框的水平对齐方向。默认 'right'：对话框右边贴齐触发按钮容器
   * 右边，向左展开（demo 中按钮位于顶栏右半段，此方向正确）。宿主把过滤
   * 按钮放在工具栏左半段时传 'left'，对话框从按钮左边向右展开，避免向
   * 左溢出视口。
   */
  align?: 'left' | 'right';
}

const TYPE_LABELS: Record<ChangeType, string> = {
  addition: '新增',
  deletion: '删除',
  modification: '修改',
  move: '移动',
  format: '格式',
};

const TYPE_DOT_CLASS: Record<ChangeType, string> = {
  addition: 'bg-change-addition-fg',
  deletion: 'bg-change-deletion-fg',
  modification: 'bg-change-modification-fg',
  move: 'bg-change-move-fg',
  format: 'bg-change-format-fg',
};

const STATUS_LABELS: Record<DecisionStatus, string> = {
  pending: '待定',
  accepted: '已接受',
  rejected: '已拒绝',
};

const SEMANTIC_TAG_LABELS: Record<SemanticTag, string> = {
  amount: '金额',
  date: '日期',
  term: '期限',
};

const CHANGE_TYPES: ChangeType[] = ['addition', 'deletion', 'modification', 'move', 'format'];
const DECISION_STATUSES: DecisionStatus[] = ['pending', 'accepted', 'rejected'];
const SEMANTIC_TAGS: SemanticTag[] = ['amount', 'date', 'term'];

function typeCount(stats: ChangeStats, type: ChangeType): number {
  switch (type) {
    case 'addition': return stats.additions;
    case 'deletion': return stats.deletions;
    case 'modification': return stats.modifications;
    case 'move': return stats.moves;
    case 'format': return stats.formats;
  }
}

export function FilterPopover({
  typeFilter,
  statusFilter,
  semanticTagFilter,
  onToggleType,
  onToggleStatus,
  onToggleSemanticTag,
  onReset,
  isFiltering,
  activeFilterCount,
  stats,
  align = 'right',
}: FilterPopoverProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when a click lands outside the popover container.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const triggerClass = isFiltering
    ? 'border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-100'
    : 'border-ink-200 bg-ink-50 text-ink-600 hover:bg-ink-100 hover:text-ink-900';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="按变更类型 / 审阅状态过滤"
        className={`inline-flex h-btn-sm items-center gap-1 rounded-sm border px-2 text-xs transition-colors duration-fast ${triggerClass}`}
      >
        <span>过滤</span>
        {isFiltering && (
          <span
            aria-label={`已激活 ${activeFilterCount} 个过滤条件`}
            className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-medium text-white"
          >
            {activeFilterCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="变更过滤"
          className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} top-full z-50 mt-1 w-36 rounded-md border border-ink-400 bg-ink-0 py-2 shadow-lg`}
        >
          {/* Change type section */}
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
            变更类型
          </p>
          {CHANGE_TYPES.map((type) => (
            <label
              key={type}
              className="flex cursor-pointer items-center gap-2 px-3 py-1 text-xs text-ink-700 hover:bg-ink-50"
            >
              <input
                type="checkbox"
                checked={typeFilter.has(type)}
                onChange={() => onToggleType(type)}
                className="h-3.5 w-3.5 flex-none rounded-sm accent-brand-500"
              />
              <span
                className={`h-2 w-2 flex-none rounded-full ${TYPE_DOT_CLASS[type]}`}
                aria-hidden="true"
              />
              <span className="whitespace-nowrap">{TYPE_LABELS[type]}</span>
              <span className="ml-auto flex-none tabular-nums text-ink-400">
                {typeCount(stats, type)}
              </span>
            </label>
          ))}

          <hr className="my-2 border-ink-100" />

          {/* Decision status section */}
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
            审阅状态
          </p>
          {DECISION_STATUSES.map((status) => (
            <label
              key={status}
              className="flex cursor-pointer items-center gap-2 px-3 py-1 text-xs text-ink-700 hover:bg-ink-50"
            >
              <input
                type="checkbox"
                checked={statusFilter.has(status)}
                onChange={() => onToggleStatus(status)}
                className="h-3.5 w-3.5 flex-none rounded-sm accent-brand-500"
              />
              <span className="whitespace-nowrap">{STATUS_LABELS[status]}</span>
            </label>
          ))}

          <hr className="my-2 border-ink-100" />

          {/* P2-5: semantic tag ("key number") section. Empty selection
              means "no constraint"; selecting tags requires every selected
              tag to be present on the change (AND semantics). */}
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
            关键数字
          </p>
          {SEMANTIC_TAGS.map((tag) => (
            <label
              key={tag}
              className="flex cursor-pointer items-center gap-2 px-3 py-1 text-xs text-ink-700 hover:bg-ink-50"
            >
              <input
                type="checkbox"
                checked={semanticTagFilter.has(tag)}
                onChange={() => onToggleSemanticTag(tag)}
                className="h-3.5 w-3.5 flex-none rounded-sm accent-brand-500"
              />
              <span className="whitespace-nowrap">{SEMANTIC_TAG_LABELS[tag]}</span>
            </label>
          ))}

          <hr className="my-2 border-ink-100" />

          {/* Reset */}
          <div className="px-3">
            <button
              type="button"
              onClick={() => {
                onReset();
                setOpen(false);
              }}
              disabled={!isFiltering}
              className="w-full rounded-sm py-1 text-xs text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              重置
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
